import { Controller } from "@hotwired/stimulus"

// Arbre des permissions du formulaire de rôle : pastilles cliquables, case
// « tout cocher » et compteur x/n par fonctionnalité, et recherche qui garde une
// fonctionnalité quand son nom — ou celui d'une sous-fonctionnalité — correspond.
// Les cases à cocher sont elles-mêmes les champs du formulaire
// (role[feature_permission_ids][]) : sans JavaScript, le formulaire reste utilisable.
export default class extends Controller {
  static targets = ["node", "chip", "checkAll", "counter", "search", "empty"]

  connect() {
    this.refresh()
  }

  // Pastilles, cases « tout cocher » et compteurs suivent l'état des cases.
  refresh() {
    this.chipTargets.forEach((chip) => {
      const checked = chip.querySelector("input").checked
      const color = chip.dataset.color
      chip.classList.toggle(`bg-${color}`, checked)
      chip.classList.toggle("bg-opacity-10", checked)
      chip.classList.toggle(`border-${color}`, checked)
      chip.classList.toggle("bg-white", !checked)
    })

    this.nodeTargets.forEach((node) => {
      const inputs = this.ownInputs(node)
      if (!inputs.length) return
      const checkedCount = inputs.filter((input) => input.checked).length
      const checkAll = this.own(node, this.checkAllTargets)
      const counter = this.own(node, this.counterTargets)
      if (checkAll) {
        checkAll.checked = checkedCount === inputs.length
        checkAll.indeterminate = checkedCount > 0 && checkedCount < inputs.length
      }
      if (counter) counter.textContent = `${checkedCount}/${inputs.length}`
    })
  }

  toggleAll(event) {
    const node = event.target.closest("[data-permission-tree-target='node']")
    this.ownInputs(node).forEach((input) => { if (!input.disabled) input.checked = event.target.checked })
    this.refresh()
  }

  reset() {
    this.chipTargets.forEach((chip) => {
      const input = chip.querySelector("input")
      if (!input.disabled) input.checked = false
    })
    this.refresh()
  }

  filter() {
    const term = this.normalize(this.searchTarget.value.trim())
    if (!term) {
      this.nodeTargets.forEach((node) => { node.hidden = false })
      this.emptyTarget.hidden = true
      return
    }
    const shown = this.rootNodes().map((root) => this.filterNode(root, term))
    this.emptyTarget.hidden = shown.some(Boolean)
  }

  // Un noeud est affiché si son nom correspond (avec tout son sous-arbre) ou si l'un
  // de ses descendants correspond (avec les seules branches concernées).
  filterNode(node, term) {
    if (this.normalize(node.dataset.name).includes(term)) {
      node.hidden = false
      this.descendants(node).forEach((descendant) => { descendant.hidden = false })
      return true
    }
    const anyChild = this.children(node).map((child) => this.filterNode(child, term)).some(Boolean)
    node.hidden = !anyChild
    return anyChild
  }

  // --- Navigation dans l'arbre (les noeuds sont imbriqués dans le DOM) ---

  parentNode(element) {
    return element.parentElement.closest("[data-permission-tree-target='node']")
  }

  rootNodes() {
    return this.nodeTargets.filter((node) => !this.parentNode(node))
  }

  children(node) {
    return this.nodeTargets.filter((other) => this.parentNode(other) === node)
  }

  descendants(node) {
    return this.nodeTargets.filter((other) => other !== node && node.contains(other))
  }

  // L'élément de `candidates` qui appartient à ce noeud, et non à une sous-fonctionnalité.
  own(node, candidates) {
    return candidates.find((element) => element.closest("[data-permission-tree-target='node']") === node)
  }

  ownInputs(node) {
    return this.chipTargets
      .filter((chip) => chip.closest("[data-permission-tree-target='node']") === node)
      .map((chip) => chip.querySelector("input"))
  }

  normalize(text) {
    return (text || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
  }
}
